import { IDriverUser, ITechList, IUserBase } from '../../types/type';
import './adminroadlist.css';
import { useState, useEffect, useMemo } from 'react';
import { fullName } from '../../utils/utils';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import { TechnicalService, TechListFilters, TechListItem } from '../../services/technical.service';
import AvatarImageCard from '../AvatarImageCard/AvatarImageCard';
import Table, {
  LabeledCell,
  LabeledSecondaryCell,
  ColumnConfig,
  ExpandedRowConfig,
  SecondaryRowConfig,
} from '../Table/Table';
import StatusCell from '../Table/StatusCell';
import Pagination from '../Pagination/Pagination';

function AdminTOList({ user }: { user: IDriverUser | IUserBase }) {
  const [techLists, setTechLists] = useState<TechListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Состояние для фильтров
  const [filters, setFilters] = useState<TechListFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
    fullName: '',
    num: '',
    checkStatus: '',
    autoparkName: '',
    mark: '',
    model: '',
    number: '',
  });

  // Состояние для пагинации
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Допущен', dotClass: 'active' },
    rejected: { label: 'Не допущен', dotClass: '' },
    waiting: { label: 'Ожидает', dotClass: 'waiting' },
  };

  // Подсчет статистики технических осмотров
  const techStats = useMemo(() => {
    const totalCount = techLists?.length || 0;
    const positiveCount = techLists?.filter((tech) => tech.checkStatus === 'completed').length || 0;
    const negativeCount = techLists?.filter((tech) => tech.checkStatus === 'rejected').length || 0;

    return {
      totalCount,
      positiveCount,
      negativeCount,
    };
  }, [techLists]);

  // Функция для обновления фильтров
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Функция для сброса фильтров
  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      startTime: '',
      endTime: '',
      fullName: '',
      num: '',
      checkStatus: '',
      autoparkName: '',
      mark: '',
      model: '',
      number: '',
    });
  };

  // Функция для изменения страницы
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'num',
      title: 'Номер заявки',
      render: (row) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'status',
      title: 'Статус',
      render: (row) => {
        const status =
          row.checkStatus === 'completed' ? 'completed' : row.checkStatus === 'rejected' ? 'rejected' : 'waiting';
        return <StatusCell status={status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null, // Рендеринг происходит внутри компонента Table
    },
  ];

  // Конфигурация вторичных строк (всегда видимых)
  const secondaryRows: SecondaryRowConfig[] = [
    {
      id: 'all-info',
      condition: (row) => true,
      content: (row) => (
        <>
          <LabeledSecondaryCell title="Автопарк" value={row.autopark?.legalName || 'Не указан'} colSpan={1} />
          <LabeledSecondaryCell title="ФИО" value={fullName(row.profile) || 'Не указан'} colSpan={2} />
          <LabeledSecondaryCell title="Номер авто" value={row.car?.number || 'Не указан'} colSpan={1} />
          <LabeledSecondaryCell
            title="Марка/модель авто"
            value={row.car ? `${row.car.mark} ${row.car.model}` : 'Не указан'}
            colSpan={1}
          />
        </>
      ),
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'parameters',
      content: (row) => (
        <>
          <LabeledCell title="Двигатель" value={row.engine ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Оптика" value={row.optic ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Кузов и салон" value={row.body ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Ходовая" value={row.chassis ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Одометр" value={row.odometerReading || '-'} />
        </>
      ),
    },
    {
      id: 'conclusion',
      content: (row) => (
        <LabeledCell
          title="Заключение"
          value={
            row.checkStatus === 'completed' ? 'Допущен' : row.checkStatus === 'rejected' ? 'Не допущен' : 'Ожидает'
          }
          colSpan={5}
        />
      ),
    },
  ];

  // Загрузка данных с учетом фильтров
  const fetchTechLists = async () => {
    if (!user || !user.id) return;

    setLoading(true);
    setError(null);

    try {
      const technicID = user.id;
      const response = await TechnicalService.getTechnicTechLists(technicID, filters);

      setTechLists(response.data);
      setPagination({
        currentPage: response.page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
      });
    } catch (err: any) {
      console.error('Ошибка при загрузке технических осмотров:', err);
      setError(err.message || 'Не удалось получить списки технических осмотров');

      // Сбрасываем данные при ошибке
      setTechLists([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Вызываем загрузку данных при изменении фильтров или пользователя
  useEffect(() => {
    fetchTechLists();
  }, [filters, user]);

  return (
    <div className={`admin-roadlist ${user?.role === 'technik' ? 'admin-roadlist__technik' : ''}`}>
      {user?.role !== 'technik' && <AvatarImageCard user={user} />}
      <div className="infoCard">
        {/* Блок фильтров */}
        <div className="techFiltersContainer">
          <div className="techFiltersRow">
            <div className="techFilterItem">
              <label>Начало периода</label>
              <input
                type="date"
                name="startTime"
                value={filters.startTime || ''}
                onChange={handleFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="techFilterItem">
              <label>Конец периода</label>
              <input
                type="date"
                name="endTime"
                value={filters.endTime || ''}
                onChange={handleFilterChange}
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div className="techFilterItem">
              <label>Результат осмотра</label>
              <select name="checkStatus" value={filters.checkStatus || ''} onChange={handleFilterChange}>
                <option value="">Все</option>
                <option value="completed">Допущен</option>
                <option value="rejected">Не допущен</option>
                <option value="waiting">Ожидает</option>
              </select>
            </div>
          </div>
          <div className="techFiltersRow">
            <div className="techFilterItem">
              <label>Автопарк</label>
              <input
                type="text"
                name="autoparkName"
                value={filters.autoparkName || ''}
                onChange={handleFilterChange}
                placeholder="Название автопарка"
              />
            </div>
            <div className="techFilterItem">
              <label>Номер заявки</label>
              <input
                type="text"
                name="num"
                value={filters.num || ''}
                onChange={handleFilterChange}
                placeholder="Номер заявки"
              />
            </div>
            <div className="techFilterItem">
              <label>Марка авто</label>
              <input
                type="text"
                name="mark"
                value={filters.mark || ''}
                onChange={handleFilterChange}
                placeholder="Марка автомобиля"
              />
            </div>
          </div>
          <div className="techFiltersRow">
            <div className="techFilterItem">
              <label>Модель авто</label>
              <input
                type="text"
                name="model"
                value={filters.model || ''}
                onChange={handleFilterChange}
                placeholder="Модель автомобиля"
              />
            </div>
            <div className="techFilterItem">
              <label>Номер авто</label>
              <input
                type="text"
                name="number"
                value={filters.number || ''}
                onChange={handleFilterChange}
                placeholder="Гос. номер автомобиля"
              />
            </div>
            <div className="techFilterItem">
              <label>ФИО водителя</label>
              <input
                type="text"
                name="fullName"
                value={filters.fullName || ''}
                onChange={handleFilterChange}
                placeholder="ФИО водителя"
              />
            </div>
          </div>
          <div className="techFiltersRow">
            <div className="techFilterItem">
              <button className="resetFiltersBtn" onClick={resetFilters}>
                Сбросить фильтры
              </button>
            </div>
          </div>
        </div>

        {/* Блок статистики технических осмотров */}
        <div className="techStatsContainer" style={{ padding: '10px 25px' }}>
          <div className="roadlist__td1-title" style={{ fontWeight: 'bold', color: '#333' }}>
            Осмотров: {techStats.totalCount}, из них Положительных {techStats.positiveCount}, Отрицательных{' '}
            {techStats.negativeCount}
          </div>
        </div>

        {loading ? (
          <div className="loadingIndicator">Загрузка...</div>
        ) : error ? (
          <div className="errorMessage">{error}</div>
        ) : (
          <>
            <div className="adm-list__container">
              <Table
                data={techLists}
                columns={tableColumns}
                expandedRows={expandedRows}
                secondaryRows={secondaryRows}
                keyField="id"
                noDataMessage="Нет данных для отображения"
              />
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminTOList;
